import React from 'react'

import { Ink } from '@acorns/web-components'
import styled from 'styled-components'

import chevronSVG from 'assets/icons/chevron-down.svg'
import { withTrackClicked } from 'components/trackers'
import { theme } from 'theme'
import { ADD_EMPLOYER_BUTTON, SEARCH_RESULT_ITEM } from 'utils/testing/testIDs'

import EmployerLogo from './EmployerLogo'

type TrackingProps = {
  employerCount: number
  employerIndex: number
  trackingId: string
  clickSwitchTargetId: string
}

type OuterProps = {
  ctaUrl: string
  employerName: string
  employerId: string
  onClick: () => void
  logoUrl?: string
  switchType: string
  requiresEmployerMailingAddress: boolean
}

export type Props = TrackingProps & OuterProps

const Container = styled.div`
  align-items: center;
  background-color: #ffffff;
  border-radius: ${theme('styles.borderRadius')};
  cursor: pointer;
  display: flex;
  padding: 12px 24px;
  position: relative;
  user-select: none;
  width: 100%;
  margin-bottom: 5px;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background-color: ${theme('colors.ivory')};

    .chevron {
      display: block;
    }
  }

  &:focus {
    outline: 0;
  }
`
const Icon = styled.div`
  align-items: center;
  background-color: #ebecec;
  border-radius: 99px;
  position: relative;
  justify-content: center;
  height: 32px;
  margin-right: 17px;
  user-select: none;
  width: 32px;

  &:before,
  &:after {
    background-color: ${theme('colors.slate')};
    border-radius: 2px;
    content: '';
    display: block;
    height: 2px;
    left: calc(50% - 6px);
    position: absolute;
    top: calc(50% - 1px);
    width: 12px;
  }

  &:after {
    transform: rotate(90deg);
  }
`

const Text = styled.div`
  color: ${theme('colors.slate')};
  font-size: 14px;
`

const Chevron = styled.img`
  transform: rotate(270deg);
  transition: transform 300ms;
  position: absolute;
  right: 43px;
  display: none;

  &:hover {
    display: block;
  }
`

export const AddEmployerButton = (props: { onClick: () => void }) => (
  <Container
    tabIndex={0}
    onClick={props.onClick}
    data-testid={ADD_EMPLOYER_BUTTON}
  >
    <Ink opacity={0.02} />
    <Icon />
    <Text>Add new employer</Text>
  </Container>
)

export const AddEmployerItem = withTrackClicked<any, any>(() => ({
  object_name: 'directDepositSearchAdd',
  screen: 'directDepositSearch',
  screen_name: 'directDepositSearch',
  destination: 'directDepositPDF',
}))(AddEmployerButton)

export const TrackedContainer = withTrackClicked<any, Props>((props) => ({
  object_name: 'directDepositSearchSelect',
  screen: 'directDepositSearch',
  screen_name: 'directDepositSearch',
  destination: 'directDepositOnline',
  index_count: `${props.employerCount}`,
  index_position_tapped: `${props.employerIndex}`,
  cta_url: props.ctaUrl,
  tracking_id: props.trackingId,
  click_switch_target_id: props.clickSwitchTargetId,
}))(Container)

export default (props: Props) => (
  <TrackedContainer
    onClick={props.onClick}
    employerCount={props.employerCount}
    employerIndex={props.employerIndex}
    employerName={props.employerName}
    employerId={props.employerId}
    ctaUrl={props.ctaUrl}
    trackingId={props.trackingId}
    clickSwitchTargetId={props.clickSwitchTargetId}
    requiresEmployerMailingAddress={props.requiresEmployerMailingAddress}
    switchType={props.switchType}
    data-employer-name={props.employerName}
    data-employer-id={props.employerId}
    data-cta-url={props.ctaUrl}
    data-click-switch-target-id={props.clickSwitchTargetId}
    data-switch-type={props.switchType}
    data-testid={SEARCH_RESULT_ITEM}
    data-require-address={props.requiresEmployerMailingAddress}
  >
    <Ink opacity={0.02} />
    <EmployerLogo src={props.logoUrl} />
    <Text>{props.employerName}</Text>
    <Chevron src={chevronSVG} className="chevron" alt="select employer" />
  </TrackedContainer>
)
